import { Box, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ComponentType } from 'react';

export type FundSetupStats = {
  totalFunds: number;
  activeFunds: number;
  disabledFunds: number;
  totalPlans: number;
};

type CardConfig = {
  key: keyof FundSetupStats;
  label: string;
  subtitle: string;
  iconSrc?: string;
  Icon?: ComponentType<{ sx?: Record<string, unknown>; 'aria-hidden'?: boolean }>;
  iconColor?: string;
  gradient: string;
};

const CARDS: CardConfig[] = [
  {
    key: 'totalFunds',
    label: 'Total Funds',
    subtitle: 'All schemes',
    iconSrc: '/images/totalfunds.svg',
    gradient: 'radial-gradient(circle at top right, rgba(32, 87, 166, 0.14) 0%, transparent 68%)',
  },
  {
    key: 'activeFunds',
    label: 'Active Funds',
    subtitle: 'Currently Active',
    Icon: CheckCircleIcon,
    iconColor: '#12B76A',
    gradient: 'radial-gradient(circle at top right, rgba(18, 183, 106, 0.16) 0%, transparent 68%)',
  },
  {
    key: 'disabledFunds',
    label: 'Disabled Funds',
    subtitle: 'Temporarily Disabled',
    iconSrc: '/images/disabled.svg',
    gradient: 'radial-gradient(circle at top right, rgba(247, 144, 9, 0.16) 0%, transparent 68%)',
  },
  {
    key: 'totalPlans',
    label: 'Total Plans',
    subtitle: 'All plans',
    iconSrc: '/images/totalplans.svg',
    gradient: 'radial-gradient(circle at top right, rgba(122, 90, 248, 0.16) 0%, transparent 68%)',
  },
];

export default function FundSetupStatsCards({ stats }: { stats: FundSetupStats }): JSX.Element {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(4, 1fr)',
        },
        gap: { xs: 1.5, sm: 2 },
        width: '100%',
        mb: { xs: 2.5, md: 3 },
      }}>
      {CARDS.map(({ key, label, subtitle, Icon, iconSrc, iconColor, gradient }) => (
        <Box
          key={key}
          sx={{
            position: 'relative',
            overflow: 'hidden',
            bgcolor: '#FFFFFF',
            borderRadius: '16px',
            boxShadow: '0px 4px 16px rgba(26, 43, 60, 0.06)',
            border: '1px solid #F2F4F7',
            p: 2.5,
            minHeight: 120,
            backgroundImage: gradient,
            backgroundRepeat: 'no-repeat',
          }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1.5,
            }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 14,
                  fontWeight: 500,
                  color: '#6B7280',
                  mb: 1,
                }}>
                {label}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 32,
                  fontWeight: 700,
                  color: '#111927',
                  lineHeight: 1.1,
                  mb: 0.75,
                }}>
                {stats[key]}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: 12,
                  fontWeight: 400,
                  color: '#9CA3AF',
                }}>
                {subtitle}
              </Typography>
            </Box>
            {iconSrc ? (
              <Box
                component="img"
                src={iconSrc}
                alt=""
                sx={{ width: 24, height: 24, flexShrink: 0 }}
              />
            ) : Icon ? (
              <Icon sx={{ fontSize: 24, color: iconColor, flexShrink: 0 }} aria-hidden />
            ) : null}
          </Box>
        </Box>
      ))}
    </Box>
  );
}
